'use client';

import useSWR from 'swr';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { paths } from 'src/routes/paths';
import { NyttFärgTema, ChipFärger } from 'src/theme/NyttFärgtema';
import axiosInstance, { endpoints } from 'src/lib/axios';

import type { KortProps } from '../kort-katalog';
import DomainCard from '../components/domain-card';
import { MiniList, MiniRow, formatKr, swrOptions } from './kort-primitiver';

// ----------------------------------------------------------------------

interface IFörskolepengPost {
    id: string;
    barnNamn?: string;
    månad?: string;
    belopp?: number;
    avstämd?: boolean;
}

interface IFörskolepengÖversikt {
    poster?: IFörskolepengPost[];
    totalBelopp?: number;
}

export default function FörskolepengKort({ orgId }: KortProps) {
    const { data, isLoading } = useSWR<IFörskolepengÖversikt>(
        orgId ? ['förskolepeng', orgId] : null,
        () => axiosInstance.get(endpoints.förskola.förskolepeng(orgId)).then((r) => r.data),
        { ...swrOptions, shouldRetryOnError: false }
    );

    const poster = data?.poster ?? [];
    const totalBelopp = data?.totalBelopp ?? poster.reduce((sum, p) => sum + (p.belopp ?? 0), 0);

    if (isLoading && !data) {
        return (
            <DomainCard
                title="Förskolepeng"
                icon="solar:hand-money-bold"
                theme={NyttFärgTema.Kombinationer[4]}
                href={paths.dashboard.förskola.förskolepeng}
                primaryMetric={{ value: '—', label: 'total' }}
            >
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (poster.length === 0) {
        return (
            <DomainCard
                title="Förskolepeng"
                icon="solar:hand-money-bold"
                theme={NyttFärgTema.Kombinationer[4]}
                href={paths.dashboard.förskola.förskolepeng}
                emptyMessage="Läs in kommunens underlag för att se förskolepengöversikten."
            />
        );
    }

    const avstämda = poster.filter((p) => p.avstämd).length;

    return (
        <DomainCard
            title="Förskolepeng"
            icon="solar:hand-money-bold"
            theme={NyttFärgTema.Kombinationer[4]}
            href={paths.dashboard.förskola.förskolepeng}
            primaryMetric={{ value: formatKr(totalBelopp), label: 'total' }}
            secondaryMetric={{ value: `${poster.length}`, label: poster.length === 1 ? 'post' : 'poster' }}
            statusChip={
                avstämda === poster.length
                    ? { label: 'AVSTÄMD', color: ChipFärger[5] }
                    : { label: `${avstämda}/${poster.length} AVSTÄMDA`, color: ChipFärger[4] }
            }
        >
            <MiniList>
                {poster.slice(0, 3).map((p) => (
                    <MiniRow
                        key={p.id}
                        primary={p.barnNamn || p.månad || 'Post'}
                        secondary={p.avstämd ? 'Avstämd' : 'Ej avstämd'}
                        trailing={p.belopp != null ? formatKr(p.belopp) : undefined}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}
